import React, { useEffect, useState } from "react";
import { Search as SearchIcon, Close as CloseIcon, ArrowDownward as ArrowDownwardIcon, ArrowUpward as ArrowUpwardIcon, KeyboardDoubleArrowDown as DoubleArrowDownIcon, ExpandMore as ExpandMoreIcon, Tune as TransformIcon } from "@mui/icons-material";
import { Box, Typography, Paper, Button, Select, MenuItem, TextField, Tooltip, IconButton, Autocomplete, InputAdornment, GlobalStyles, Fab } from "@mui/material";
import { useNavigate, useLocation } from 'react-router-dom';
import ROUTES from '../../main-layout/RoutesPath';
import { removeTempState } from '../../../utils/navigationTemp';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import OutputFileWizardLayout from "./OutputFileWizardLayout";
import { updateReturnFile, fetchReturnConfig, updateOutputFormat } from "../../../services/api";
import toast from "react-hot-toast";
import useLoading from "../../../hooks/useLoading";
import { useDispatch, useSelector } from "react-redux";
import { setOutputFormatResponse } from "../../../redux/reducers/adminConfigSlice";
import ColumnTransformDialog, { describeTransforms } from "./ColumnTransformDialog";

function getRuleSetColumnMapping(sheet) {
  if (!sheet) return [];
  const ruleSetColumnMapping = sheet.ruleSetColumnMapping || sheet.RuleSetColumnMapping || [];
  const ruleSetsStr = sheet.ruleSets || sheet.RuleSets || "";
  const ruleSets = ruleSetsStr.split(",").map(rs => rs.trim()).filter(Boolean);
  const mappingByName = Object.fromEntries(
    ruleSetColumnMapping.map(rs => [rs.ruleSetName || rs.RuleSetName, rs])
  );

  return ruleSets.map(ruleSetName => {
    const existing = mappingByName[ruleSetName] || { ruleSetName, columnMappings: [] };
    const rawMappings = existing.columnMappings || existing.ColumnMappings || [];
    return {
      ruleSetName: existing.ruleSetName || existing.RuleSetName || ruleSetName,
      columnMappings: rawMappings.map(m => ({
        colRef: m.colRef || m.ColRef,
        columnHeader: m.columnHeader || m.ColumnHeader,
        providerProperty: m.providerProperty || m.ProviderProperty,
        custom: m.custom || m.Custom,
        replacements: m.replacements || m.Replacements || null,
        transforms: m.transforms || m.Transforms || null
      }))
    };
  });
}

export default function ConfigureOutputMapping({ onCancel, onSave, onFinish, mappingData, initialValues, providerFilterData, tabReconFilterData, onNavigateStep, onExit }) {
 const outputFormatResponse = useSelector(state => state.config?.outputFormatResponse);
  const combinedData = outputFormatResponse?.response_data || mappingData?.response_data || initialValues || {};
  const reportSheetsRaw = React.useMemo(() => {
    let defJson = combinedData?.definition_json;
    if (typeof defJson === 'string') {
      try {
        defJson = JSON.parse(defJson);
      } catch (e) {
        defJson = null;
      }
    }

    return combinedData?.config?.reportSheets ||
      combinedData?.config?.ReportSheets ||
      defJson?.ReportSheets ||
      defJson?.reportSheets ||
      initialValues?.sheets || [];
  }, [combinedData, initialValues?.sheets]);

  const reportSheets = React.useMemo(() => reportSheetsRaw.map(sheet => ({
    ...sheet,
    name: sheet.name || sheet.Name,
    mappingNeed: sheet.mappingNeed ?? sheet.MappingNeed ?? true,
    ruleName: sheet.ruleName || sheet.RuleName || sheet.name,
    templateRow: sheet.templateRow ?? sheet.TemplateRow ?? 0,
    headerRow: sheet.headerRow ?? sheet.HeaderRow ?? sheet.columnHeaderRow ?? sheet.ColumnHeaderRow ?? 1,
    reportType: sheet.reportType ?? sheet.ReportType,
    columnInfos: (sheet.columnInfos || sheet.ColumnInfos || []).map(col => ({
      ...col,
      colRef: col.colRef || col.ColRef,
      columnHeader: col.columnHeader || col.ColumnHeader
    })),
    ruleSets: sheet.ruleSets || sheet.RuleSets || "",
    ruleSetColumnMapping: sheet.ruleSetColumnMapping || sheet.RuleSetColumnMapping || []
  })), [reportSheetsRaw]);

  const [selectedTabIdx, setSelectedTabIdx] = React.useState(0);
  const [ruleConfig, setRuleConfig] = useState(null);
  const { withLoading } = useLoading();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tempKey = location.state?.tempKey;
  const filteredSheets = React.useMemo(() => reportSheets.filter(sheet => sheet.mappingNeed), [reportSheets]);
  const sheetNames = React.useMemo(() => filteredSheets.map(sheet => sheet.name), [filteredSheets]);
  const selectedSheetName = sheetNames[selectedTabIdx];
  const selectedSheet = filteredSheets.find(sheet => sheet.name === selectedSheetName);
  const selectedRuleSetColumnMapping = getRuleSetColumnMapping(selectedSheet);
  const selectedColumns = selectedSheet?.columnInfos || [];
  const mappingOptions = outputFormatResponse?.response_data?.mapping || mappingData?.response_data?.mapping || combinedData?.mapping || [];
  const [tabMappings, setTabMappings] = useState({});
  const [tabProviderProperties, setTabProviderProperties] = useState({});
  // How each column's value is written, by sheet and then by `${ruleSetName}-${colRef}`: its Transforms,
  // and any Replacements an earlier editor saved (kept as they are until the column is given transforms).
  const [tabTransforms, setTabTransforms] = useState({});
  const [transformTarget, setTransformTarget] = useState(null);
  const tabMappingsRef = React.useRef(tabMappings);
  const tabProviderPropertiesRef = React.useRef(tabProviderProperties);
  const tabTransformsRef = React.useRef(tabTransforms);
  const skipReinitRef = React.useRef(false);
  const selectedTabRef = React.useRef(null);
  React.useEffect(() => {
    tabMappingsRef.current = tabMappings;
    tabProviderPropertiesRef.current = tabProviderProperties;
    tabTransformsRef.current = tabTransforms;
  });

  const setColumnTransforms = (sheetName, cellKey, entry) => {
    setTabTransforms(prev => {
      const sheet = { ...(prev[sheetName] || {}) };
      if (entry && ((entry.transforms && entry.transforms.length > 0) || (entry.replacements && Object.keys(entry.replacements).length > 0)))
        sheet[cellKey] = entry;
      else
        delete sheet[cellKey];
      return { ...prev, [sheetName]: sheet };
    });
  };
  const selectedMappings = tabMappings[selectedSheetName] || {};
  const selectedProviderProperties = tabProviderProperties[selectedSheetName] || {};
  const mappedCount = selectedColumns.filter(col => selectedMappings[col.colRef]).length;

  // Keep the active sheet tab in view when the strip scrolls (20+ sheets).
  useEffect(() => {
    selectedTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedTabIdx, selectedSheetName]);

  const [scrollState, setScrollState] = useState('top');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY;
      const totalHeight = document.body.scrollHeight - window.innerHeight;

      if (totalHeight <= 0) return;

      if (scrollPos < totalHeight * 0.2) {
        setScrollState('top');
      } else if (scrollPos > totalHeight * 0.8) {
        setScrollState('bottom');
      } else {
        setScrollState('mid');
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const totalCount = selectedColumns.length;
  const [searchUploadedPayerFile, setSearchUploadedPayerFile] = useState('');
  const filteredUploadedColumns = React.useMemo(() => {
    if (!searchUploadedPayerFile) return selectedColumns;
    const term = searchUploadedPayerFile.toLowerCase();
    return selectedColumns.filter(col =>
      col.columnHeader?.toLowerCase().includes(term) ||
      col.colRef?.toLowerCase().includes(term)
    );
  }, [searchUploadedPayerFile, selectedColumns]);

  const handleMappingChange = (colRef, value) => {
    const cleanValue = String(value || "").trim();
    if (cleanValue === "Select" || cleanValue === "") {
      setTabMappings(prev => ({ ...prev, [selectedSheetName]: { ...(prev[selectedSheetName] || {}), [colRef]: "" } }));
      setTabProviderProperties(prev => ({ ...prev, [selectedSheetName]: { ...(prev[selectedSheetName] || {}), [colRef]: "" } }));
      return;
    }
    const selectedOption = mappingOptions.find(option => (option.csvFieldName || "").trim().toLowerCase() === cleanValue.toLowerCase());
    const entityProperty = selectedOption?.entityProperty || "";
    setTabMappings(prev => ({
      ...prev,
      [selectedSheetName]: {
        ...(prev[selectedSheetName] || {}),
        [colRef]: value
      }
    }));
    setTabProviderProperties(prev => ({
      ...prev,
      [selectedSheetName]: {
        ...(prev[selectedSheetName] || {}),
        [colRef]: entityProperty
      }
    }));
  };

  useEffect(() => {
    const data = outputFormatResponse?.response_data || mappingData?.response_data || initialValues;
    const config = data?.config || data?.definition_json;
    if (config) {
      setRuleConfig(config);
    }
  }, [outputFormatResponse, mappingData, initialValues]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
        .ag-theme-alpine .ag-popup .ag-rich-select {
          border-radius: 8px !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .ag-theme-alpine .ag-popup .ag-rich-select-value {
          border: none !important;
          min-width: 250px !important;
          width: 250px !important;
          max-width: 250px !important;
          font-size: 11px !important;
          background: #fdfdfd !important;
          padding: 10px 12px !important;
        }
        .ag-theme-alpine .ag-popup .ag-rich-select-list {
          border: none !important;
          font-size: 11px !important;
        }
        .ag-theme-alpine .ag-popup .ag-rich-select-item {
          padding: 10px 14px !important;
          font-size: 11px !important;
          transition: background 0.2s ease;
        }
        .ag-theme-alpine .ag-popup .ag-rich-select-item:hover {
          background-color: #f0f4f8 !important;
        }
        .ag-theme-alpine .ag-popup .ag-rich-select-item.ag-rich-select-item-selected {
          background-color: #e3f2fd !important;
          color: #1976d2 !important;
          font-weight: 500 !important;
        }
      `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const initialTabMappings = {};
    const initialTabProviderProperties = {};
    const initialTabTransforms = {};
    filteredSheets.forEach(sheet => {
      const mappings = {};
      const providerProps = {};
      const transforms = {};
      const ruleSetColumnMapping = getRuleSetColumnMapping(sheet);
      ruleSetColumnMapping.forEach(ruleSet => {
        (ruleSet.columnMappings || []).forEach(mapping => {
          const cellKey = `${ruleSet.ruleSetName}-${mapping.colRef}`;
          const hasTransforms = Array.isArray(mapping.transforms) && mapping.transforms.length > 0;
          const hasReplacements = mapping.replacements && Object.keys(mapping.replacements).length > 0;
          if (hasTransforms || hasReplacements) {
            transforms[cellKey] = {
              transforms: hasTransforms ? mapping.transforms : null,
              replacements: hasReplacements ? { ...mapping.replacements } : null
            };
          }
          if (
            typeof mapping.custom === "string" &&
            mapping.custom.toLowerCase() === "custom"
          ) {
            mappings[cellKey] = "Custom";
            providerProps[cellKey + "_constant"] = mapping.providerProperty || "";
          } else {
            const searchProp = mapping.providerProperty || "";
            const matchedOption = mappingOptions.find(m =>
              (m.entityProperty && String(m.entityProperty).toLowerCase() === String(searchProp).toLowerCase()) ||
              (m.csvFieldName && String(m.csvFieldName).trim().toLowerCase() === String(searchProp).toLowerCase())
            );
            mappings[cellKey] = (matchedOption?.csvFieldName || "").trim();
            providerProps[cellKey] = matchedOption?.entityProperty || searchProp;
          }
        });
      });
      initialTabMappings[sheet.name] = mappings;
      initialTabProviderProperties[sheet.name] = providerProps;
      initialTabTransforms[sheet.name] = transforms;
    });

    if (skipReinitRef.current) {
      skipReinitRef.current = false;
      return;
    }
    setTabMappings(initialTabMappings);
    setTabProviderProperties(initialTabProviderProperties);
    setTabTransforms(initialTabTransforms);
  }, [outputFormatResponse, mappingData, mappingOptions, filteredSheets]);
  const handleTabChange = (idx) => {
    setSelectedTabIdx(idx);
  };

  // The data transformation button of a mapped cell: orange when the column has transforms.
  const renderTransformButton = (cellKey, columnLabel) => {
    const entry = (tabTransforms[selectedSheetName] || {})[cellKey];
    const summary = describeTransforms(entry?.transforms, entry?.replacements);
    return (
      <Tooltip title={summary ? `Data transformation: ${summary}` : "Add data transformation"} arrow>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            setTransformTarget({ sheetName: selectedSheetName, cellKey, columnLabel });
          }}
          aria-label="data-transformation"
          sx={{ p: 0.4 }}
        >
          <TransformIcon sx={{ fontSize: 16, color: summary ? '#FF8200' : '#bdbdbd' }} />
        </IconButton>
      </Tooltip>
    );
  };

  function CustomInputCell({ value, onValueChange, onClear, extra }) {
    const [localValue, setLocalValue] = React.useState(value || '');
    const inputRef = React.useRef(null);

    React.useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key !== 'Tab') {
          e.stopPropagation();
        }
      };

      const inputElement = inputRef.current;
      if (inputElement) {
        inputElement.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        if (inputElement) {
          inputElement.removeEventListener('keydown', handleKeyDown);
        }
      };
    }, []);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1, pl: 1, py: '8px', width: '100%' }}>
        <Typography sx={{ fontSize: '11px', color: '#344049', pr: 1, minWidth: 52 }}>Custom</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flex: 1, pr: 0.5 }}>
          <TextField
            inputRef={inputRef}
            variant="standard"
            size="small"
            value={localValue}
            onChange={e => setLocalValue(e.target.value)}
            onBlur={e => {
              if (localValue !== (value || '')) {
                onValueChange(localValue);
              }
            }}
            placeholder="Enter value"
            InputProps={{
              disableUnderline: false,
              style: {
                fontSize: '11px',
                background: 'transparent',
                padding: 0,
                minWidth: 90,
                border: 'none',
              }
            }}
            sx={{
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
              p: 0,
              m: 0,
              flex: 1
            }}
            onClick={e => e.stopPropagation()}
          />
          {extra}
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setLocalValue('');
              onValueChange('');
              if (typeof onClear === 'function') onClear();
            }}
            aria-label="clear-custom"
            sx={{ p: 0.4 }}
          >
            <CloseIcon sx={{ color: localValue ? '#969696' : '#cfcfcf', fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }


  const handleSave = async (skipRefresh = false) => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    await new Promise(resolve => setTimeout(resolve, 0));
    const currentTabMappings = tabMappingsRef.current;
    const currentTabProviderProperties = tabProviderPropertiesRef.current;

    const emptyCustomFields = [];
    reportSheets.forEach(sheet => {
      const sheetMappings = currentTabMappings[sheet.name] || {};
      const sheetProviderProperties = currentTabProviderProperties[sheet.name] || {};
      getRuleSetColumnMapping(sheet).forEach(ruleSet => {
        (sheet.columnInfos || []).forEach(col => {
          const cellKey = `${ruleSet.ruleSetName}-${col.colRef}`;
          if (
            typeof sheetMappings[cellKey] === "string" &&
            sheetMappings[cellKey].toLowerCase() === "custom" &&
            !(sheetProviderProperties[cellKey + "_constant"]?.trim())
          ) {
            emptyCustomFields.push(`${sheet.name} → ${col.columnHeader || col.colRef}`);
          }
        });
      });
    });
    if (emptyCustomFields.length > 0) {
      toast.error(`Custom field required: ${emptyCustomFields[0]}`);
      return false;
    }

    const currentTabTransforms = tabTransformsRef.current;
    const updatedReportSheets = reportSheets.map(sheet => {
      const sheetMappings = currentTabMappings[sheet.name] || {};
      const sheetProviderProperties = currentTabProviderProperties[sheet.name] || {};
      const sheetTransforms = currentTabTransforms[sheet.name] || {};
      const sheetColumns = sheet.columnInfos || [];
      const ruleSetColumnMapping = getRuleSetColumnMapping(sheet).map(ruleSet => {
        const columnMappings = sheetColumns
          .filter(col => {
            const cellKey = `${ruleSet.ruleSetName}-${col.colRef}`;
            return sheetMappings[cellKey] !== undefined && sheetMappings[cellKey] !== "";
          })
          .map(col => {
            const cellKey = `${ruleSet.ruleSetName}-${col.colRef}`;
            const selectedMapping = sheetMappings[cellKey];
            const isCustom =
              typeof selectedMapping === "string" &&
              selectedMapping.toLowerCase() === "custom";
            const columnTransforms = sheetTransforms[cellKey];
            return {
              ColRef: col.colRef,
              ColumnHeader: col.columnHeader,
              ProviderProperty: isCustom
                ? sheetProviderProperties[cellKey + "_constant"] || ""
                : sheetProviderProperties[cellKey] || "",
              Custom: isCustom ? "Custom" : null,
              ...(columnTransforms?.replacements && Object.keys(columnTransforms.replacements).length > 0
                ? { Replacements: columnTransforms.replacements } : {}),
              ...(columnTransforms?.transforms && columnTransforms.transforms.length > 0
                ? { Transforms: columnTransforms.transforms } : {})
            };
          });

        return {
          RuleSetName: ruleSet.ruleSetName,
          ColumnMappings: columnMappings
        };
      });

      const tabFilters = tabReconFilterData?.[sheet.name] || [];
      const activeTabFilters = tabFilters.filter(row =>
        row.propertyPath || row.condition || (Array.isArray(row.values) && row.values.length > 0)
      );
      let sheetProviderDataFilters;
      if (activeTabFilters.length > 0) {
        sheetProviderDataFilters = activeTabFilters.map(item => ({
          GroupName: item.group || "",
          PropertyPath: item.propertyPath || "",
          GroupCondition: item.logic || "",
          DataFilterValues: Array.isArray(item.values) ? item.values.join(",") : String(item.values || ""),
          DataFilterCondition: item.condition || ""
        }));
      } else {
        sheetProviderDataFilters = sheet.ProviderDataFilters || sheet.providerDataFilters || [];
      }

      return {
        Name: sheet.name,
        RuleName: sheet.ruleName || sheet.name,
        TemplateRow: sheet.templateRow || 0,
        HeaderRow: sheet.headerRow || 1,
        MappingNeed: sheet.mappingNeed !== undefined ? sheet.mappingNeed : true,
        ReportType: sheet.reportType,
        RuleSetColumnMapping: ruleSetColumnMapping,
        RuleSets: sheet.ruleSets || "",
        columnInfos: (sheet.columnInfos || []).map(col => ({
          ColRef: col.colRef,
          ColumnHeader: col.columnHeader
        })),
        ...(sheetProviderDataFilters.length > 0 && { ProviderDataFilters: sheetProviderDataFilters })
      };
    });
    const activeFilterRows = (providerFilterData || [])
      .filter(row => row.propertyPath || row.condition || (Array.isArray(row.values) && row.values.length > 0));

    let providerDataFilters = [];
    if (activeFilterRows.length > 0) {
      providerDataFilters = activeFilterRows.map(item => ({
        GroupName: item.group || "",
        PropertyPath: item.propertyPath || "",
        GroupCondition: item.logic || "",
        DataFilterValues: Array.isArray(item.values) ? item.values.join(",") : String(item.values || ""),
        DataFilterCondition: item.condition || ""
      }));
    } else {
      try {
        const defJson = typeof initialValues?.definition_json === 'string'
          ? JSON.parse(initialValues.definition_json)
          : (initialValues?.definition_json || {});
        providerDataFilters = defJson?.ProviderDataFilters || [];
      } catch (e) {
        providerDataFilters = [];
      }
    }

    const definitionJson = {
      Name: initialValues?.name || "Default Name",
      GroupPlan: initialValues?.selectedHealthPlans || [],
      ReportSheets: updatedReportSheets,
      ...(providerDataFilters.length > 0 && { ProviderDataFilters: providerDataFilters })
    };

    let changesRuleJson = initialValues?.changes_rule_json;
    if (changesRuleJson != null && changesRuleJson !== '' && typeof changesRuleJson !== "string") {
      changesRuleJson = JSON.stringify(changesRuleJson);
    }

    const payload = new FormData();

   payload.append("definition_json", JSON.stringify(definitionJson));
    if (changesRuleJson != null && changesRuleJson !== '') {
      payload.append("changes_rule_json", changesRuleJson);
    }
    payload.append("health_plan_id", initialValues?.health_plan_id || 0);
    payload.append("id", initialValues?.id || combinedData?.id || 0);

    await withLoading(async () => {
      try {
        const response = await updateReturnFile(payload);
        if (response?.is_successful) {
          toast.success(typeof response.response_data === 'string' ? response.response_data : (response.user_message || "Saved successfully."));
      
          const refreshPayload = reportSheets.map(row => ({
            sheetName: row.name,
            ruleName: row.ruleName || row.name,
            templateRow: row.templateRow || 0,
            columnHeaderRow: row.headerRow || 0,
            mappingNeed: row.mappingNeed,
            healthPlanId: initialValues?.health_plan_id || 0,
            report_type: row.reportType,
            ruleSet: row.ruleSets
          }));
          if (!skipRefresh) {
            updateOutputFormat(refreshPayload)
              .then(() => fetchReturnConfig(initialValues?.health_plan_id))
              .then(res => { if (res) { skipReinitRef.current = true; dispatch(setOutputFormatResponse(res)); } })
              .catch(console.error);
          }

          if (onSave) onSave();
        } else {
          toast.error(response.user_message || "Save failed.");
        }
      } catch (error) {
        toast.error("Error saving configuration. Please try again.");
      }
    });
    return true;
  };

  const handleFinish = async () => {
    const saveSuccessful = await handleSave(true);
    if (saveSuccessful && onFinish) { onFinish(); }
  };

  const handleCancel = () => { if (onCancel) onCancel(); };

  const CustomCellEditor = React.forwardRef((props, ref) => {
    const inputRef = React.useRef(null);
    const [value, setValue] = React.useState(props.value || '');

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    React.useImperativeHandle(ref, () => ({
      getValue: () => value,
      isCancelAfterEnd: () => false,
    }));

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1, pl: 1, py: '8px' }}>
        <Typography sx={{ fontSize: '11px', color: '#344049', pr: 1, minWidth: 52 }}>Custom</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            inputRef={inputRef}
            variant="standard"
            size="small"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="Enter value"
            InputProps={{
              disableUnderline: false,
              sx: {
                '&:before': {
                  borderBottom: '1px solid #bdbdbd !important',
                },
                '&:after': {
                  borderBottom: '1px solid #bdbdbd !important',
                },
              },
              style: {
                fontSize: '11px',
                background: 'transparent',
                padding: 0,
                minWidth: 90,
                border: 'none',
              }
            }}
            sx={{
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
              p: 0,
              m: 0,
            }}
            onClick={e => e.stopPropagation()}
          />
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setValue('');
            }}
            aria-label="clear-custom-editor"
            sx={{ p: 0.4 }}
          >
            <CloseIcon sx={{ color: value ? '#969696' : '#cfcfcf', fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    );
  });

  return (
    <OutputFileWizardLayout
      activeStep={3}
      name={initialValues?.name}
      onBack={onCancel}
      onExit={onExit}
      onStepSelect={onNavigateStep}
      instruction="Map each column from the uploaded payer file to the matching PDM field. Choose Custom to enter a fixed value or expression. Use the tabs to map each sheet."
      footer={
        <>
          <Button
            variant="outlined"
            sx={{ mr: "auto", borderColor: "#B0B0B0", color: "#344049", background: "#fff" }}
            onClick={handleCancel}
          >
            Back
          </Button>
          <Button
            variant="text"
            sx={{ color: "#6b7684", "&:hover": { backgroundColor: "#e6e6e6" } }}
            onClick={() => {
              try {
                if (tempKey) removeTempState(tempKey);
              } catch (e) {
                console.warn('ConfigureOutputMapping: failed to remove temp state', e);
              }
              navigate(ROUTES.ADMIN_CONFIG, { state: { selectedItem: 'healthPlan' } });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            sx={{ color: "#FF8200", borderColor: "#FF8200", background: "#fff", "&:hover": { borderColor: "#e67500", color: "#e67500", background: "#fff" } }}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#FF8200", color: "#fff", "&:hover": { backgroundColor: "#e67500" } }}
            onClick={handleFinish}
          >
            Finish
          </Button>
        </>
      }
    >
    <Box sx={{ background: "#fafbfc", px: { xs: 1, sm: 3 }, pb: 4, pt: 2, position: 'relative', minWidth: 0 }}>
      <GlobalStyles
        styles={{
          '.ag-left-aligned-header .ag-header-cell-label': {
            justifyContent: 'flex-start !important',
          },
          '.ag-theme-alpine .ag-header': {
            backgroundColor: '#e9ecef !important',
            height: '32px !important',
            minHeight: '32px !important',
          },
          '.ag-theme-alpine .ag-header-row': {
            height: '32px !important',
            minHeight: '32px !important',
          },
          '.ag-theme-alpine .ag-header-cell': {
            fontSize: '11px !important',
            fontWeight: '600 !important',
            color: '#344049 !important',
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          borderBottom: "1px solid #E5E7EB",
          mb: 2,
          // Many sheets (20+) overflow the pane — scroll the strip instead of the page.
          overflowX: "auto",
          flexWrap: "nowrap",
          maxWidth: "100%",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": { background: "#C7CDD4", borderRadius: 3 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        {sheetNames.map((sheet, idx) => (
          <Tooltip key={sheet} title={sheet} arrow>
            <Box
              ref={idx === selectedTabIdx ? selectedTabRef : undefined}
              sx={{
                px: 2,
                pb: 1,
                borderBottom: idx === selectedTabIdx ? "3px solid #FF8200" : "3px solid transparent",
                color: idx === selectedTabIdx ? "#FF8200" : "#666666",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 80,
                maxWidth: 150,
                flexShrink: 0,
              }}
              onClick={() => handleTabChange(idx)}
            >
              <Typography sx={{
                textAlign: "center",
                lineHeight: 1.2,
                fontSize: '14px',
                fontWeight: idx === selectedTabIdx ? 500 : 400,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {sheet}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', pb: 2, pt: 2, pl: 1 }}>
        <Typography sx={{ fontWeight: 600, color: '#344049', fontSize: '14px' }}>
          {(() => {
            const sheet = filteredSheets[selectedTabIdx];
            let mapped = 0, total = 0;
            if (sheet) {
              const sheetColumns = sheet?.columnInfos || [];
              const sheetMappings = tabMappings[sheet.name || sheet.Name] || {};
              mapped = sheetColumns.filter(col => {
                const ruleSetColumnMapping = getRuleSetColumnMapping(sheet);
                return ruleSetColumnMapping.some(rs => {
                  const cellKey = `${rs.ruleSetName}-${col.colRef}`;
                  return sheetMappings[cellKey] !== undefined && sheetMappings[cellKey] !== "";
                });
              }).length;
              total = sheetColumns.length;
            }
            return `Map fields of Payer output file with PDM  - Monitoring (${mapped}/${total})`;
          })()}
        </Typography>
        <TextField
          variant="standard"
          size="small"
          placeholder="Search uploaded payer file"
          sx={{ minWidth: 240, mr: 2 }}
          value={searchUploadedPayerFile || ''}
          onChange={e => setSearchUploadedPayerFile(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setSearchUploadedPayerFile('');
              e.stopPropagation();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#969696', fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={e => {
                    e.stopPropagation();
                    setSearchUploadedPayerFile('');
                  }}
                  aria-label="clear"
                  disabled={!searchUploadedPayerFile}
                  sx={{ visibility: 'visible', p: 0.4 }}
                >
                  <CloseIcon sx={{ color: searchUploadedPayerFile ? '#969696' : '#cfcfcf', fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              'input::placeholder': {
                fontSize: '11px',
              }
            }
          }}
        />
      </Box>

      <Box sx={{ width: '100%', mb: 3, fontSize: '11px', overflowX: 'auto' }}>
        <div className="ag-theme-alpine" style={{ width: '100%', minWidth: 0 }}>
          <AgGridReact
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
              wrapHeader: false,
              autoHeaderHeight: false,
            }}
            rowData={filteredUploadedColumns}
            headerHeight={32}
            rowHeight={40}
            columnDefs={selectedRuleSetColumnMapping.length > 0 ? [
              {
                headerName: 'Fields from Uploaded Payer file (Column Ref)',
                headerClass: 'ag-left-aligned-header',
                wrapHeader: false,
                autoHeaderHeight: false,
                field: 'columnHeader',
                flex: 1,
                minWidth: 150,
                valueGetter: params => `${params.data.columnHeader} (${params.data.colRef})`,
                cellStyle: {
                  color: '#344049',
                  fontSize: '11px',
                  padding: '10px 0px 10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  height: '100%',
                  textAlign: 'left',
                },
                suppressMenu: true,
                sortable: true,
                filter: false,
              },
              ...selectedRuleSetColumnMapping.map((rs, idx) => ({
                headerName: rs.ruleSetName,
                field: rs.ruleSetName,
                wrapHeader: false,
                autoHeaderHeight: false,
                width: 250,
                editable: true,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: () => ({
                  values: [
                    'Select',
                    ...mappingOptions.map(item => item.csvFieldName),
                    ...(!mappingOptions.some(opt => opt.csvFieldName === 'Custom') ? ['Custom'] : [])
                  ],
                  allowTyping: true,
                  filterList: true,
                  highlightMatch: true,
                  valueListMaxHeight: 220
                }),
                cellEditorPopup: true,
                valueGetter: params => {
                  const col = params.data;
                  const safeRuleSetName = rs.ruleSetName || `RuleSet_${idx}`;
                  const cellKey = `${safeRuleSetName}-${col.colRef}`;
                  const mapping = rs.columnMappings.find(cm => cm.colRef === col.colRef);
                  return selectedMappings[cellKey] !== undefined
                    ? selectedMappings[cellKey]
                    : (mapping?.providerProperty || '');
                },
                valueSetter: params => {
                  if (params.newValue === undefined || params.newValue === params.oldValue) return false;
                  const col = params.data;
                  const safeRuleSetName = rs.ruleSetName || `RuleSet_${idx}`;
                  const cellKey = `${safeRuleSetName}-${col.colRef}`;
                  handleMappingChange(cellKey, params.newValue);
                  return true;
                },
                cellRenderer: p => {
                  const col = p.data;
                  const safeRuleSetName = rs.ruleSetName || `RuleSet_${idx}`;
                  const cellKey = `${safeRuleSetName}-${col.colRef}`;
                  const isCustom = typeof selectedMappings[cellKey] === 'string' && selectedMappings[cellKey].toLowerCase() === 'custom';
                  if (isCustom) {
                    return (
                      <CustomInputCell
                        extra={renderTransformButton(cellKey, `${col.columnHeader} (${col.colRef}) – ${safeRuleSetName}`)}
                        value={selectedProviderProperties[cellKey + '_constant'] || ''}
                        onValueChange={val => {
                          setTabProviderProperties(prev => ({
                            ...prev,
                            [selectedSheetName]: {
                              ...(prev[selectedSheetName] || {}),
                              [cellKey + '_constant']: val
                            }
                          }));
                        }}
                        onClear={() => {
                          setColumnTransforms(selectedSheetName, cellKey, null);
                          setTabProviderProperties(prev => ({
                            ...prev,
                            [selectedSheetName]: {
                              ...(prev[selectedSheetName] || {}),
                              [cellKey + '_constant']: ''
                            }
                          }));
                          setTabMappings(prev => ({
                            ...prev,
                            [selectedSheetName]: {
                              ...(prev[selectedSheetName] || {}),
                              [cellKey]: ''
                            }
                          }));
                        }}
                      />
                    );
                  }
                  return (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      justifyContent: 'space-between',
                      pr: 0.5,
                      cursor: 'pointer',
                      width: '100%'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '11px',
                        color: (p.value && p.value !== 'Select') ? '#344049' : '#969696',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {(() => {
                          const val = (p.value && p.value !== 'Select')
                            ? p.value
                            : (() => {
                              const col = p.data;
                              const safeRuleSetName = rs.ruleSetName || `RuleSet_${idx}`;
                              const cellKey = `${safeRuleSetName}-${col.colRef}`;
                              const prop = selectedProviderProperties[cellKey];
                              if (prop) {
                                const found = mappingOptions.find(m => String(m.entityProperty).toLowerCase() === String(prop).toLowerCase());
                                return found?.csvFieldName || 'Select';
                              }
                              return 'Select';
                            })();

                          return val;
                        })()}
                      </Box>

                      {(!p.value || p.value === 'Select') ? (
                        <ExpandMoreIcon sx={{ fontSize: 20, color: '#bdbdbd', ml: 0.5 }} />
                      ) : (
                        <>
                        {renderTransformButton(cellKey, `${col.columnHeader} (${col.colRef}) – ${safeRuleSetName}`)}
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            const col = p.data;
                            const safeRuleSetName = rs.ruleSetName || `RuleSet_${idx}`;
                            const cellKey = `${safeRuleSetName}-${col.colRef}`;
                            setColumnTransforms(selectedSheetName, cellKey, null);
                            setTabMappings(prev => ({
                              ...prev,
                              [selectedSheetName]: {
                                ...(prev[selectedSheetName] || {}),
                                [cellKey]: ''
                              }
                            }));
                            setTabProviderProperties(prev => ({
                              ...prev,
                              [selectedSheetName]: {
                                ...(prev[selectedSheetName] || {}),
                                [cellKey]: ''
                              }
                            }));
                          }}
                          aria-label="clear-mapping"
                          sx={{ p: 0.4, '&:hover': { color: '#d32f2f' } }}
                        >
                          <CloseIcon sx={{ color: '#969696', fontSize: 16 }} />
                        </IconButton>
                        </>
                      )}
                    </Box>
                  );
                },
                suppressMenu: true,
                sortable: true,
                filter: false,
              })),
            ] : []}
            domLayout="autoHeight"
            suppressRowHoverHighlight={true}
            suppressContextMenu={true}
            suppressMovableColumns={true}
            getRowStyle={params => ({ background: params.node.rowIndex % 2 === 0 ? '#fff' : '#f7f7f7' })}
            overlayNoRowsTemplate={`<div style='display: flex; align-items: center; justify-content: center; color: #666; font-size: 11px;'>No columns available for this search ....</div>`}
          />
        </div>
      </Box>

      <ColumnTransformDialog
        open={!!transformTarget}
        columnLabel={transformTarget?.columnLabel || ''}
        transforms={transformTarget ? (tabTransforms[transformTarget.sheetName] || {})[transformTarget.cellKey]?.transforms : null}
        replacements={transformTarget ? (tabTransforms[transformTarget.sheetName] || {})[transformTarget.cellKey]?.replacements : null}
        onClose={() => setTransformTarget(null)}
        onRemove={() => {
          setColumnTransforms(transformTarget.sheetName, transformTarget.cellKey, null);
          setTransformTarget(null);
        }}
        onSave={transforms => {
          // Saved transforms take over from any Replacements the column had: the dialog showed those as its value map.
          setColumnTransforms(transformTarget.sheetName, transformTarget.cellKey, { transforms, replacements: null });
          setTransformTarget(null);
        }}
      />


      {/* <Tooltip title={
        scrollState === 'top' ? "Scroll to Middle" :
          scrollState === 'mid' ? "Scroll to Bottom" :
            "Scroll to Top"
      }>
        <Fab
          size="small"
          aria-label="scroll-toggle"
          onClick={() => {
            const totalHeight = document.body.scrollHeight;
            if (scrollState === 'top') {
              window.scrollTo({ top: totalHeight / 2, behavior: 'smooth' });
            } else if (scrollState === 'mid') {
              window.scrollTo({ top: totalHeight, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 8,
            right: 10,
            backgroundColor: '#fff',
            color: '#FF8200',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
            border: "1px solid #FF8200",
            zIndex: 1000,
            width: 32,
            height: 32,
            minHeight: 0,
            '&:hover': {
              backgroundColor: '#fff9f2',
            }
          }}
        >
          {scrollState === 'top' && <ArrowDownwardIcon sx={{ fontSize: 18 }} />}
          {scrollState === 'mid' && <DoubleArrowDownIcon sx={{ fontSize: 18 }} />}
          {scrollState === 'bottom' && <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
        </Fab>
      </Tooltip> */}
    </Box>
    </OutputFileWizardLayout>
  );
}